import { NavigateOptions, To } from 'react-router-dom';
import { CampaignExternalType, CampaignListItem, CampaignType } from '../types';

export const CAMPAIGN_BUILDER_BASE_PATH = '/campaign-builder';

export const campaignBuilderPath = (suffix = '') => `${CAMPAIGN_BUILDER_BASE_PATH}${suffix}`;

export const formatTypeName = (type: string, externalType: string): string => {
  if (type === 'annual' && externalType === 'custom') {
    return 'Annual';
  }

  if (type === 'annual' && externalType === 'birthday') {
    return 'Birthday';
  }

  if (type === 'reminder' && externalType === 'appointment') {
    return 'Calendar Event Reminder';
  }

  if (type === 'message' && externalType === 'flyer') {
    return 'Text Flyer';
  }

  if (type === 'message' && externalType === 'custom') {
    return 'Bulk Message';
  }

  if (type === 'join' && externalType === 'custom') {
    return 'Custom Join';
  }

  if (type === 'join' && externalType === 'text_to_join') {
    return 'Text-To-Join';
  }

  if (type === 'join' && externalType === 'contact_to_group') {
    return 'Contact-To-Group';
  }

  if (type === 'enrollment' && externalType === 'custom') {
    return 'Enrollment';
  }

  if (type === 'recurring' && externalType === 'custom') {
    return 'Recurring Campaign';
  }

  return '';
};

export const toCampaignCreate = <
  T extends { type: CampaignType; externalType?: CampaignExternalType; configId?: string | null },
>(
  navigate: (to: To, options?: NavigateOptions) => void,
  campaign: T,
) => {
  // For duplication, navigate to campaign builder with campaign data
  const duplicateCampaignId = (campaign as unknown as CampaignListItem).id;
  if (duplicateCampaignId) {
    const configIdOrType = campaign.configId || campaign.type;
    return navigate(campaignBuilderPath(`/${configIdOrType}`), {
      state: { duplicateCampaignId },
    });
  }

  // For new campaign creation, use configId if available, otherwise use type
  const configIdOrType = campaign.configId || campaign.type;
  return navigate(campaignBuilderPath(`/${configIdOrType}`));
};

export const toCampaignEdit = <
  T extends { id: string; name: string; type: CampaignType; externalType: CampaignExternalType; configId?: string | null },
>(
  navigate: (to: To, options?: NavigateOptions) => void,
  campaign: T,
) => {
  const path = campaign.configId
    ? campaignBuilderPath(`/${campaign.configId}/${campaign.id}`)
    : campaignBuilderPath(`/${campaign.type}/${campaign.id}`);

  // Analytics metrics only exist on list items. When the caller passes a leaner
  // shape (e.g. a group's joinCampaigns entry) none of the metric fields are
  // present — carrying an empty analytics object would make the builder render
  // a zeroed placeholder, so only attach it when the shape actually carries
  // metrics. Checking the full metric set (not just two fields) keeps this
  // robust when a real campaign legitimately has zero/null on some of them.
  const item = campaign as unknown as Partial<CampaignListItem>;
  const hasAnalytics = [
    item.numberOfContacts,
    item.totalReplies,
    item.numberOfRespondedContacts,
    item.lastMessageSentAt,
    item.lastResponseAt,
    item.messagesDelivered,
    item.messagesPending,
    item.messagesFailed,
  ].some(value => value !== undefined);
  if (!hasAnalytics) {
    return navigate(path);
  }

  const campaignAnalytics = {
    id: campaign.id,
    name: campaign.name,
    numberOfContacts: item.numberOfContacts,
    totalReplies: item.totalReplies,
    numberOfRespondedContacts: item.numberOfRespondedContacts,
    lastMessageSentAt: item.lastMessageSentAt,
    lastResponseAt: item.lastResponseAt,
    scheduledMessages: item.scheduledMessages,
    messagesDelivered: item.messagesDelivered,
    messagesPending: item.messagesPending,
    messagesFailed: item.messagesFailed,
  };

  return navigate(path, { state: { campaignAnalytics } });
};

export const getDropdownType = (type: string, externalType?: string): string => {
  if (type === '') return 'all';

  if (type === 'annual' && externalType === 'custom') return 'annual_custom';
  if (type === 'annual' && externalType === 'birthday') return 'annual_birthday';
  if (type === 'reminder' && externalType === 'appointment') return 'reminder_appointment';
  if (type === 'message' && externalType === 'flyer') return 'message_flyer';
  if (type === 'message' && externalType === 'custom') return 'message_custom';
  if (type === 'join') return 'join';
  if (type === 'enrollment') return 'enrollment';
  if (type === 'recurring' && externalType === 'custom') return 'recurring';
  // if (type === "join" && externalType === "custom") return "join_custom";
  // if (type === "join" && externalType === "text_to_join") return "text_to_join";
  // if (type === "join" && externalType === "contact_to_group") return "contact_to_group";

  return '';
};

export const saveDropdownType = (selectedType: string): { type: string; external_type: string } => {
  switch (selectedType) {
    case 'annual_custom':
      return { type: 'annual', external_type: 'custom' };
    case 'annual_birthday':
      return { type: 'annual', external_type: 'birthday' };
    case 'reminder_appointment':
      return { type: 'reminder', external_type: 'appointment' };
    case 'message_flyer':
      return { type: 'message', external_type: 'flyer' };
    case 'message_custom':
      return { type: 'message', external_type: 'custom' };
    case 'join':
      return { type: 'join', external_type: '' };
    case 'enrollment':
      return { type: 'enrollment', external_type: 'custom' };
    case 'recurring':
      return { type: 'recurring', external_type: 'custom' };
    // case "join_custom":
    //   return { type: "join", external_type: "custom" };
    // case "text_to_join":
    //   return { type: "join", external_type: "text_to_join" };
    // case "contact_to_group":
    //   return { type: "join", external_type: "contact_to_group" };
    default:
      return { type: '', external_type: '' };
  }
};

export const typeOptions = [
  { value: 'all', title: 'All' },
  { value: 'annual_custom', title: 'Annual' },
  { value: 'annual_birthday', title: 'Birthday' },
  { value: 'reminder_appointment', title: 'Calendar Event Reminder' },
  { value: 'message_flyer', title: 'Text Flyer' },
  { value: 'message_custom', title: 'Bulk Message' },
  { value: 'join', title: 'Join' },
  { value: 'enrollment', title: 'Enrollment' },
  { value: 'recurring', title: 'Recurring Campaign' },
  // { value: "join_custom", title: "Custom Join" },
  // { value: "text_to_join", title: "Text-To-Join" },
  // { value: "contact_to_group", title: "Contact-To-Group" },
];
